import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Users, UserCheck, Clock, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/Motion";
import { UserDialog } from "@/components/admin/UserDialog";
import { ImportStudentsDialog } from "@/components/admin/ImportStudentsDialog";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { api, errMsg, LEVELS } from "@/lib/api";
import { countryName } from "@/lib/countries";
import { UserFacetFilters } from "@/components/admin/UserFacetFilters";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value, id }) => (
  <div className="bg-white border border-[#E5DCCB] p-5 flex items-center justify-between" data-testid={`admin-stat-${id}`}>
    <div><p className="text-sm text-stone-600">{label}</p><p className="font-display text-4xl font-bold">{value ?? 0}</p></div>
    <Icon className="w-7 h-7 text-[#8B3A2B]" />
  </div>
);

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [school, setSchool] = useState("");
  const [country, setCountry] = useState("");
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: users.length, active: users.filter((u) => u.is_active).length,
    pending: users.filter((u) => !u.is_active).length, admins: users.filter((u) => u.role === "teacher").length,
  }), [users]);

  const unpaid = (u) => u.role === "user" && !u.subscription_until;
  const unpaidCount = users.filter(unpaid).length;

  const shown = users.filter((u) =>
    (filter === "all" || (filter === "unpaid" ? unpaid(u) : filter === "active" ? u.is_active : !u.is_active)) &&
    (!school || u.school === school) && (!country || u.country === country) &&
    (`${u.name} ${u.username || ""} ${u.email} ${u.phone} ${u.school || ""} ${countryName(u.country)}`.toLowerCase().includes(q.toLowerCase())));

  const toggle = async (u, v) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/status`, { is_active: v });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, ...data, ok: undefined } : x)));
      toast.success(v ? `تم تفعيل حساب ${u.name}` : `تم تعطيل حساب ${u.name}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (dialog.user) { await api.put(`/admin/users/${dialog.user.id}`, payload); toast.success("تم حفظ التعديلات"); }
      else { await api.post("/admin/users", payload); toast.success("تم إنشاء الحساب"); }
      setDialog({ open: false, user: null });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      toast.success(`تم حذف ${toDelete.name}`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" dir="rtl" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-sm font-semibold text-[#B45309] mb-2">لوحة الإدارة</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold"><MaskLines lines={["إدارة المسجلين"]} delay={0.1} /></h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setImportOpen(true)} data-testid="admin-import-students-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#047857] text-[#047857] px-5 py-3 font-semibold hover:bg-[#047857] hover:text-white transition-colors">
              <FileSpreadsheet className="w-5 h-5" /> إضافة طلاب من ملف
            </button>
            <button onClick={() => setDialog({ open: true, user: null })} data-testid="admin-create-user-modal-trigger"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8B3A2B] text-[#FAF7F2] px-6 py-3 font-semibold hover:bg-[#702E22] transition-colors">
              <Plus className="w-5 h-5" /> إضافة حساب
            </button>
          </div>
        </div>

        <AdminTabs />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users} label="إجمالي المسجلين" value={stats.total} id="total" />
          <Stat icon={UserCheck} label="حسابات مفعّلة" value={stats.active} id="active" />
          <Stat icon={Clock} label="بانتظار التفعيل" value={stats.pending} id="pending" />
          <Stat icon={ShieldCheck} label="المعلمون" value={stats.admins} id="teachers" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو البريد أو الهاتف أو المدرسة" data-testid="admin-search-input"
              className="w-full h-11 pr-10 pl-4 bg-white border border-[#E5DCCB] rounded-sm focus:outline-none focus:border-[#8B3A2B]" />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {[["all", "الكل"], ["pending", "بانتظار التفعيل"], ["active", "مفعّل"], ["unpaid", `سجّل ولم يشترك (${unpaidCount})`]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
                className={`px-4 h-11 rounded-full text-sm border whitespace-nowrap transition-colors ${filter === k ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "bg-white border-[#E5DCCB] hover:border-[#1C1917]"}`}>{l}</button>
            ))}
          </div>
        </div>

        <UserFacetFilters users={users} school={school} country={country} onSchool={setSchool} onCountry={setCountry} />

        <div className="bg-white border border-[#E5DCCB] overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-users-table">
            <thead className="bg-[#F2ECE1] text-stone-700">
              <tr>{["المستخدم", "الهاتف", "المدرسة", "الدولة", "المستوى", "الدور", "التفعيل", "إجراءات"].map((h) => <th key={h} className="text-right font-semibold px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id} className="border-t border-[#E5DCCB] hover:bg-[#FAF7F2] transition-colors" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-4"><p className="font-semibold">{u.name}</p><p className="text-stone-600 text-xs" dir="ltr" data-testid={`admin-user-username-${u.id}`}>{u.username ? `@${u.username}` : ""}{u.username && u.email ? " · " : ""}{u.email}</p></td>
                  <td className="px-4 py-4 text-stone-700" dir="ltr">{u.phone || "—"}</td>
                  <td className="px-4 py-4 text-stone-700" data-testid={`admin-user-school-${u.id}`}>{u.school || "—"}</td>
                  <td className="px-4 py-4 text-stone-700" data-testid={`admin-user-country-${u.id}`}>{countryName(u.country) || "—"}</td>
                  <td className="px-4 py-4">{u.level} · {LEVELS.find((l) => l.n === u.level)?.name}</td>
                  <td className="px-4 py-4"><span className={`text-xs px-2.5 py-1 rounded-full ${u.role === "admin" ? "bg-[#8B3A2B] text-white" : u.role === "teacher" ? "bg-[#047857] text-white" : "bg-[#F2ECE1]"}`}>{u.role === "admin" ? "مدير" : u.role === "teacher" ? "معلم" : "طالب"}</span></td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Switch dir="ltr" checked={u.is_active} disabled={u.id === me?.id} onCheckedChange={(v) => toggle(u, v)} data-testid={`admin-toggle-activate-btn-${u.id}`} />
                      <span className={`text-xs ${u.is_active ? "text-emerald-700" : "text-amber-700"}`}>{u.is_active ? "مفعّل" : "بانتظار التفعيل"}</span>
                    </div>
                    {u.role === "user" && u.subscription_until && (
                      <p className={`text-[11px] mt-1 ${u.subscription_until > new Date().toISOString() ? "text-stone-600" : "text-red-700 font-semibold"}`} data-testid={`admin-user-subscription-${u.id}`}>
                        {u.subscription_until > new Date().toISOString() ? "الاشتراك حتى " : "انتهى الاشتراك "}{u.subscription_until.slice(0, 10)}
                      </p>
                    )}
                    {u.role === "user" && !u.subscription_until && (
                      <p className="text-[11px] mt-1 text-amber-800 font-semibold" data-testid={`admin-user-unpaid-${u.id}`}>لم يشترك بعد</p>
                    )}
                    {u.role === "teacher" && (
                      <p className="text-[11px] mt-1 text-emerald-800" data-testid={`admin-user-levels-${u.id}`}>
                        {u.levels?.length ? `المستويات: ${u.levels.join("، ")}` : "كل المستويات"}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-1">
                      <button onClick={() => setDialog({ open: true, user: u })} data-testid={`admin-edit-user-btn-${u.id}`} className="p-2 rounded-full hover:bg-[#F2ECE1]" aria-label="تعديل"><Pencil className="w-4 h-4" /></button>
                      <button disabled={u.id === me?.id} onClick={() => setToDelete(u)} data-testid={`admin-delete-user-btn-${u.id}`} className="p-2 rounded-full text-red-800 hover:bg-red-50 disabled:opacity-30" aria-label="حذف"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shown.length && <p className="p-8 text-center text-stone-600" data-testid="admin-empty">لا توجد نتائج.</p>}
        </div>
      </div>

      <UserDialog open={dialog.open} initial={dialog.user} saving={saving} onSubmit={save} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} />
      <ImportStudentsDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent dir="rtl" className="bg-[#FAF7F2]" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right font-display text-2xl">حذف الحساب؟</AlertDialogTitle>
            <AlertDialogDescription className="text-right">سيتم حذف حساب «{toDelete?.name}» نهائيًا ولا يمكن التراجع.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="admin-delete-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-800 hover:bg-red-900" data-testid="admin-delete-confirm">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
